// axios is in devDependencies but should be in dependencies
import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS, HTTP_STATUS } from '../constants';
import ErrorHandler from '../utils/errorHandler';

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if (token) {
    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    };
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Handle standardized API responses
    if (response.data && typeof response.data === 'object') {
      // If response has standardized format, extract data
      if ('success' in response.data && response.data.success === true) {
        const { data, message, success, ...rest } = response.data;

        // Prefer the `data` envelope when present; otherwise return remaining fields
        // (e.g. endpoints that return `token` at top-level).
        const normalizedData = typeof data === 'undefined' ? rest : data;

        return { ...response, data: normalizedData, message };
      }
      // Handle error responses
      if ('success' in response.data && response.data.success === false) {
        const errorMessage =
          response.data?.error?.message || response.data?.message || 'Request failed';
        const error = new Error(errorMessage);
        error.response = response;
        throw error;
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      delete api.defaults.headers.common.Authorization;
      window.location.href = '/login';
    }

    // Extract error message from standardized response
    const errorMessage =
      error.response?.data?.error?.message || error.response?.data?.message || error.message;

    const enhancedError = new Error(errorMessage);
    enhancedError.response = error.response;
    enhancedError.config = error.config;
    enhancedError.request = error.request;

    ErrorHandler.logError(enhancedError, 'API');

    return Promise.reject(enhancedError);
  }
);

export default api;
